import React, { useEffect, useRef, useState } from 'react';
import { TAB_NAV_EVENT } from '../services/tabNavigation';

/**
 * `.salon-tabs` — onglets de la fiche salon.
 *
 * La maquette se contentait de `role="tab"` + `aria-selected` et d'un attribut
 * `hidden` booléen : ni `aria-controls`, ni `role="tablist"` correctement
 * relié aux panneaux, ni navigation clavier. Le motif ARIA complet est
 * implémenté ici (flèches, Home/End, roving tabindex) — aucun pixel ne bouge.
 *
 * Deep-link : `#tab-<id>` ouvre l'onglet correspondant au chargement, et la
 * sélection met le fragment à jour sans empiler d'entrée d'historique.
 *
 * `goToTab()` (liens rapides du panneau identité) arrive par l'évènement
 * `TAB_NAV_EVENT` plutôt que par le hash : le hash passait par `hashchange`,
 * dont l'ordonnancement face au `requestAnimationFrame` qui portait le défilement
 * n'est pas garanti — le scroll tombait sur un panneau encore `hidden`, donc
 * sans effet. Ici l'activation et le défilement sont dans le même flux React :
 * `scrollRequest` s'incrémente à chaque demande (donc même quand l'onglet visé
 * est déjà actif) et l'effet qui défile ne tourne qu'**après** le commit, quand
 * le panneau existe réellement dans la page. La cible est la section entière,
 * comme `goToTab()` de la maquette, et non le panneau : viser le panneau laissait
 * la barre d'onglets hors écran.
 */
const Tabs = ({ tabs = [], initialTab, ariaLabel = 'Informations du salon', scrollTargetId }) => {
  const [active, setActive] = useState(initialTab || (tabs[0] && tabs[0].id));
  const [scrollRequest, setScrollRequest] = useState(0);
  const buttonRefs = useRef({});

  // L'écouteur est posé une fois pour toutes ; il lit la liste courante par ref
  // pour ne pas se ré-abonner à chaque rendu (les onglets sont recréés à chaque
  // passe par `LocalPage`).
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  useEffect(() => {
    const exists = (id) => id && tabsRef.current.some((tab) => tab.id === id);

    const fromHash = (window.location.hash || '').replace('#tab-', '');
    if (exists(fromHash)) setActive(fromHash);

    const onHashChange = () => {
      const id = (window.location.hash || '').replace('#tab-', '');
      if (exists(id)) setActive(id);
    };

    const onGoToTab = (event) => {
      const id = event.detail && event.detail.tabId;
      if (!exists(id)) return;
      setActive(id);
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', `#tab-${id}`);
      }
      setScrollRequest((count) => count + 1);
    };

    window.addEventListener('hashchange', onHashChange);
    document.addEventListener(TAB_NAV_EVENT, onGoToTab);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      document.removeEventListener(TAB_NAV_EVENT, onGoToTab);
    };
  }, []);

  // Après commit : le panneau demandé est affiché, la section est mesurable.
  useEffect(() => {
    if (!scrollRequest || !scrollTargetId) return;
    const section = document.getElementById(scrollTargetId);
    if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [scrollRequest, scrollTargetId]);

  const select = (id, { focus = false } = {}) => {
    setActive(id);
    if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
      window.history.replaceState(null, '', `#tab-${id}`);
    }
    if (focus && buttonRefs.current[id]) buttonRefs.current[id].focus();
  };

  const onKeyDown = (event) => {
    const index = tabs.findIndex((tab) => tab.id === active);
    if (index < 0) return;

    const moves = {
      ArrowRight: (index + 1) % tabs.length,
      ArrowLeft: (index - 1 + tabs.length) % tabs.length,
      Home: 0,
      End: tabs.length - 1,
    };
    const next = moves[event.key];
    if (next === undefined) return;

    event.preventDefault();
    select(tabs[next].id, { focus: true });
  };

  return (
    <>
      <div className="salon-tabs-nav" role="tablist" aria-label={ariaLabel} onKeyDown={onKeyDown}>
        {tabs.map((tab) => {
          const isActive = tab.id === active;
          return (
            <button
              key={tab.id}
              type="button"
              id={`tabbtn-${tab.id}`}
              className={['salon-tab-btn', isActive ? 'active' : null].filter(Boolean).join(' ')}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tab-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              ref={(node) => {
                buttonRefs.current[tab.id] = node;
              }}
              onClick={() => select(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {tabs.map((tab) => (
        <div
          key={tab.id}
          className="salon-tab-panel"
          id={`tab-${tab.id}`}
          role="tabpanel"
          aria-labelledby={`tabbtn-${tab.id}`}
          tabIndex={0}
          hidden={tab.id !== active}
        >
          {tab.content}
        </div>
      ))}
    </>
  );
};

export default Tabs;
